import { Box, useRecipe } from '@chakra-ui/react'
import { clsx } from 'clsx'

import { statusBadgeRecipe } from 'theme/recipes/statusBadge.recipe'

import type { RecipeVariantProps } from '@chakra-ui/react'
import type { HTMLAttributes, ReactNode } from 'react'

type StatusBadgeRecipeVariants = RecipeVariantProps<typeof statusBadgeRecipe>

export type StatusTone = NonNullable<StatusBadgeRecipeVariants['tone']>

type KnownStatus =
  'active' | 'failed' | 'missing' | 'pending' | 'preparing' | 'processing' | 'ready' | 'not set' | 'gcs uri'

interface StatusBadgeProperties extends Omit<HTMLAttributes<HTMLSpanElement>, 'style'> {
  children?: ReactNode
  status?: string
  tone?: StatusTone
}

const STATUS_TO_TONE: Partial<Record<KnownStatus, StatusTone>> = {
  active: 'success',
  failed: 'danger',
  missing: 'danger',
  pending: 'warning',
  preparing: 'warning',
  processing: 'warning',
  ready: 'success',
  'not set': 'danger',
  'gcs uri': 'info',
}

const normalizeStatus = (status: string) => status.trim().toLowerCase()

const isKnownStatus = (value: string): value is KnownStatus => {
  return Object.prototype.hasOwnProperty.call(STATUS_TO_TONE, value)
}

export const StatusBadge = ({ children, className, status, tone, ...properties }: StatusBadgeProperties) => {
  /* --- Section 1: Hooks --- */
  const recipe = useRecipe({ recipe: statusBadgeRecipe })

  /* --- Section 3: Derived Values --- */
  const normalized = status ? normalizeStatus(status) : undefined
  const mappedTone = normalized && isKnownStatus(normalized) ? STATUS_TO_TONE[normalized] : undefined
  const resolvedTone = tone ?? mappedTone ?? 'neutral'
  const styles = recipe({ tone: resolvedTone })

  /* --- Section 6: Render --- */
  return (
    <Box as="span" {...properties} className={clsx(className)} css={styles} data-tone={resolvedTone}>
      {children ?? status}
    </Box>
  )
}
